import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ChevronsLeft, ChevronsRight, LogOut, MoreHorizontal, X } from "lucide-react";
import { Logo, LogoMark } from "./Logo";
import { LangToggle, useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";

const linkCls = (collapsed) => ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${isActive ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-[0_0_15px_rgba(59,130,246,0.3)]" : "text-slate-400 hover:bg-white/5 hover:text-white"} ${collapsed ? "justify-center" : ""}`;

export default function AppShell({ items, bottom = [], badge }) {
  const [collapsed, setCollapsed] = useState(false);
  const [more, setMore] = useState(false);
  const { t } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => {
    await logout();
    nav("/login");
  };
  return (
    <div className="flex min-h-screen bg-[#05070A] text-slate-200">
      <aside className={`sticky top-0 hidden h-screen shrink-0 flex-col bg-[#0A0D14] border-r border-white/5 transition-[width] duration-300 lg:flex ${collapsed ? "w-[80px]" : "w-64"}`} data-testid="app-sidebar">
        <div className="flex h-20 items-center justify-between px-5">
          {collapsed ? <LogoMark className="mx-auto h-8 w-8" /> : <Logo light small to={items[0].to} />}
        </div>
        {!collapsed && badge && <div className="mx-5 mb-3 rounded-lg bg-white/5 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">{badge}</div>}
        <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-2">
          {items.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} className={linkCls(collapsed)} data-testid={`side-${i.id}`} title={t(i.en, i.hi)}>
              <i.icon size={18} className="shrink-0" />
              {!collapsed && <span className="truncate">{t(i.en, i.hi)}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1 border-t border-white/5 p-4">
          <button onClick={doLogout} className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-400 hover:bg-white/5 hover:text-white transition-colors ${collapsed ? "justify-center" : ""}`} data-testid="logout-btn">
            <LogOut size={18} /> {!collapsed && t("Logout", "लॉगआउट")}
          </button>
          <button onClick={() => setCollapsed(!collapsed)} className="flex w-full items-center justify-center rounded-xl py-2 text-slate-500 hover:text-white transition-colors" data-testid="sidebar-collapse-btn">
            {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          </button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-20 items-center justify-between gap-3 border-b border-white/5 bg-[#05070A]/80 px-4 backdrop-blur-xl sm:px-6">
          <div className="lg:hidden"><Logo small light to={items[0].to} /></div>
          <div className="hidden min-w-0 lg:block">
            <p className="truncate text-sm font-bold text-white" data-testid="header-user-name">{user?.name}</p>
            <p className="text-xs text-slate-500" data-testid="header-partner-id">{user?.partner_id} • {String(user?.role || "").replace(/_/g, " ")}</p>
          </div>
          <div className="flex items-center gap-3">
            <LangToggle />
            <button onClick={doLogout} className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white lg:hidden" data-testid="logout-btn-mobile" aria-label="Logout"><LogOut size={18} /></button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 pb-28 pt-8 sm:px-6 lg:px-8 lg:pb-10">
          <Outlet />
        </main>
      </div>
      <nav className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-5 border-t border-white/5 bg-[#0A0D14]/90 backdrop-blur-xl lg:hidden" data-testid="bottom-nav">
        {bottom.map((i) => (
          <NavLink key={i.to} to={i.to} end={i.end} data-testid={`bottom-${i.id}`} className={({ isActive }) => `flex flex-col items-center gap-1 py-3 text-[10px] font-bold ${isActive ? "text-[#D4AF37]" : "text-slate-500"}`}>
            <i.icon size={20} />
            <span className="truncate">{t(i.en, i.hi)}</span>
          </NavLink>
        ))}
        <button onClick={() => setMore(true)} className="flex flex-col items-center gap-1 py-3 text-[10px] font-bold text-slate-500" data-testid="bottom-more">
          <MoreHorizontal size={20} />
          {t("More", "और")}
        </button>
      </nav>
      {more && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setMore(false)}>
          <div className="absolute inset-x-0 bottom-0 max-h-[75vh] overflow-y-auto rounded-t-[2rem] bg-[#0A0D14] border-t border-white/10 p-6" onClick={(e) => e.stopPropagation()} data-testid="more-sheet">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-bold text-white">{t("Menu", "मेनू")}</p>
              <button onClick={() => setMore(false)} className="text-slate-400" data-testid="more-close"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.end} onClick={() => setMore(false)} data-testid={`more-${i.id}`} className={({ isActive }) => `flex flex-col items-center gap-2 rounded-2xl border p-4 text-center text-[11px] font-bold transition-colors ${isActive ? "border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/5 bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white"}`}>
                  <i.icon size={20} />
                  {t(i.en, i.hi)}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
